/**
 * SP Campaign creation contract (ADP-103) — shared by the API and the wizard.
 *
 * The capability object is the ONLY source of validation thresholds: the wizard
 * must not hard-code a budget floor, a bid range, or a placement ceiling. Amazon
 * quantifies almost none of these at runtime (it says "lower than the minimum
 * required by the marketplace" without a number), so the values are maintained
 * server-side and shipped to the client through this schema.
 */
import { z } from "zod";

// ── Capabilities ────────────────────────────────────────────────

/** Whether a capability can actually be written for this marketplace. */
export const CapabilitySupportSchema = z.enum(["writable", "unsupported"]);
export type CapabilitySupport = z.infer<typeof CapabilitySupportSchema>;

/**
 * Where a marketplace's numeric limits came from. `probed` means they were
 * observed against a real account; `conservative_default` means the marketplace
 * has not been probed and the values are deliberately tight — the wizard should
 * lean on Amazon's own validation errors rather than promising precision.
 */
export const LimitsSourceSchema = z.enum(["probed", "conservative_default"]);
export type LimitsSource = z.infer<typeof LimitsSourceSchema>;

/** Amazon bidding strategy — API enum values, never the localized label. */
export const BiddingStrategySchema = z.enum([
  // Dynamic bids — down only
  "LEGACY_FOR_SALES",
  // Dynamic bids — up and down
  "AUTO_FOR_SALES",
  // Fixed bids
  "MANUAL",
]);
export type BiddingStrategy = z.infer<typeof BiddingStrategySchema>;

/** Placement slots that accept a percentage adjustment. */
export const PlacementSchema = z.enum([
  "PLACEMENT_TOP",
  "PLACEMENT_PRODUCT_PAGE",
  "PLACEMENT_REST_OF_SEARCH",
  // Amazon Business is not a separate switch — it is one more placement entry.
  "SITE_AMAZON_BUSINESS",
]);
export type Placement = z.infer<typeof PlacementSchema>;

/** The four auto target groups the wizard exposes. */
const AUTO_TARGET_GROUP_VALUES = [
  "close_match",
  "loose_match",
  "substitutes",
  "complements",
] as const;
export const AutoTargetGroupSchema = z.enum(AUTO_TARGET_GROUP_VALUES);
export type AutoTargetGroup = z.infer<typeof AutoTargetGroupSchema>;

export const CampaignCreationCapabilitiesSchema = z.object({
  marketplaceId: z.string(),
  marketplaceName: z.string(),
  currencyCode: z.string(),
  limitsSource: LimitsSourceSchema,
  campaignName: z.object({ maxLength: z.number().int().positive() }),
  dailyBudget: z.object({
    min: z.number().positive(),
    max: z.number().positive(),
    decimals: z.number().int().nonnegative(),
  }),
  bid: z.object({
    min: z.number().positive(),
    max: z.number().positive(),
    decimals: z.number().int().nonnegative(),
  }),
  placementAdjustment: z.object({
    min: z.number().int().nonnegative(),
    /** 900 is stated outright by Amazon's request-level validation. */
    max: z.number().int().positive(),
    placements: z.array(PlacementSchema),
  }),
  limits: z.object({
    maxProducts: z.number().int().positive(),
    maxNegativeKeywords: z.number().int().positive(),
    maxNegativeProducts: z.number().int().positive(),
    /** Amazon rejects any batch over this outright (request-level 400). */
    batchMaxEntries: z.number().int().positive(),
  }),
  biddingStrategies: z.array(BiddingStrategySchema),
  features: z.object({
    amazonBusinessPlacement: CapabilitySupportSchema,
    audienceBidding: CapabilitySupportSchema,
    offAmazonSettings: CapabilitySupportSchema,
  }),
});
export type CampaignCreationCapabilities = z.infer<typeof CampaignCreationCapabilitiesSchema>;

// ── Submission payload ──────────────────────────────────────────

/**
 * Auto-targeting bids. Exactly one mode is submitted — `default` sets the ad
 * group's default bid and writes no targets at all, while `per_target_group`
 * updates the state and optional bids of the four clauses Amazon generates itself.
 */
const AutoTargetSettingSchema = z
  .object({
    enabled: z.boolean(),
    bid: z.number().positive().nullable(),
  })
  .superRefine((setting, ctx) => {
    if (setting.enabled && setting.bid === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "An enabled auto target requires a bid",
        path: ["bid"],
      });
    }
  });

const AutoTargetSettingsSchema = z
  .object({
    close_match: AutoTargetSettingSchema,
    loose_match: AutoTargetSettingSchema,
    substitutes: AutoTargetSettingSchema,
    complements: AutoTargetSettingSchema,
  })
  .superRefine((targets, ctx) => {
    if (!Object.values(targets).some((target) => target.enabled)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one auto target group must be enabled",
      });
    }
  });

const CanonicalAutoTargetingBidsSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("default"), defaultBid: z.number().positive() }),
  z.object({
    mode: z.literal("per_target_group"),
    targets: AutoTargetSettingsSchema,
  }),
]);

function normalizeLegacyAutoTargetingBids(value: unknown): unknown {
  if (!value || typeof value !== "object") return value;
  const candidate = value as Record<string, unknown>;
  if (candidate.mode !== "per_target_group" || !candidate.bids || candidate.targets) return value;
  if (typeof candidate.bids !== "object") return value;
  const bids = candidate.bids as Record<string, unknown>;
  return {
    mode: "per_target_group",
    targets: Object.fromEntries(
      AUTO_TARGET_GROUP_VALUES.map((group) => [group, { enabled: true, bid: bids[group] }]),
    ),
  };
}

/** Legacy `bids` payloads are normalized so persisted in-flight requests remain resumable. */
export const AutoTargetingBidsSchema = z.preprocess(
  normalizeLegacyAutoTargetingBids,
  CanonicalAutoTargetingBidsSchema,
);
export type AutoTargetingBids = z.infer<typeof AutoTargetingBidsSchema>;

/** Positive manual keyword match types accepted by the SP v3 keyword endpoint. */
export const ManualKeywordMatchTypeSchema = z.enum(["BROAD", "PHRASE", "EXACT"]);
export type ManualKeywordMatchType = z.infer<typeof ManualKeywordMatchTypeSchema>;

export const ManualProductTargetSchema = z.union([
  z
    .object({
      asin: z
        .string()
        .trim()
        .regex(/^[A-Z0-9]{10}$/),
      matchType: z.enum(["exact", "expanded"]).optional(),
      bid: z.number().positive(),
    })
    .strict(),
  z.object({ categoryId: z.string().trim().regex(/^\d+$/), bid: z.number().positive() }).strict(),
  z
    .object({
      categoryId: z.string().trim().regex(/^\d+$/),
      brandId: z.string().trim().min(1).max(128),
      bid: z.number().positive(),
    })
    .strict(),
]);
export type ManualProductTarget = z.infer<typeof ManualProductTargetSchema>;

export const ManualTargetingSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("keyword"),
    defaultBid: z.number().positive(),
    targets: z
      .array(
        z.object({
          text: z.string().trim().min(1),
          matchType: ManualKeywordMatchTypeSchema,
          bid: z.number().positive(),
        }),
      )
      .min(1),
  }),
  z.object({
    type: z.literal("product"),
    defaultBid: z.number().positive(),
    targets: z.array(ManualProductTargetSchema).min(1),
  }),
  // Template portfolio (docs/tech/12): category refinement + product-category theme,
  // both written as MANUAL SP targeting clauses (§8, verified on a live account 2026-10-13).
  z.object({
    type: z.literal("category"),
    defaultBid: z.number().positive(),
    targets: z
      .array(
        z.object({
          categoryId: z.string().trim().min(1),
          priceGreaterThan: z.number().nonnegative().optional(),
          priceLessThan: z.number().positive().optional(),
          ratingGreaterThan: z.number().min(0).max(5).optional(),
          ratingLessThan: z.number().min(0).max(5).optional(),
          bid: z.number().positive(),
        }),
      )
      .min(1),
  }),
  z.object({
    type: z.literal("theme"),
    defaultBid: z.number().positive(),
    targets: z
      .array(
        z.object({
          theme: z.literal("KEYWORDS_RELATED_TO_YOUR_PRODUCT_CATEGORY"),
          bid: z.number().positive(),
        }),
      )
      .min(1),
  }),
]);
export type ManualTargeting = z.infer<typeof ManualTargetingSchema>;

const CampaignCreationAdGroupBaseSchema = z.object({
  name: z.string().trim().min(1),
  /**
   * Seller accounts advertise by SKU — a product ad carrying only an ASIN is
   * rejected with `missingValueError` at `$.productAds[].sku`. ASIN is kept
   * for display and for vendor accounts.
   */
  products: z
    .array(z.object({ asin: z.string().min(1), sku: z.string().min(1).nullable() }))
    .min(1),
});

const CampaignCreationAdGroupSchema = z.preprocess(
  (value) => {
    if (!value || typeof value !== "object" || "targetingType" in value) return value;
    // Persisted pre-manual drafts and in-flight requests contained only this
    // field. Normalize them so resumes keep the original automatic semantics.
    return { ...(value as Record<string, unknown>), targetingType: "automatic" };
  },
  z.discriminatedUnion("targetingType", [
    CampaignCreationAdGroupBaseSchema.extend({
      targetingType: z.literal("automatic"),
      autoTargetingBids: AutoTargetingBidsSchema,
    }),
    CampaignCreationAdGroupBaseSchema.extend({
      targetingType: z.literal("manual"),
      manualTargeting: ManualTargetingSchema,
    }),
  ]),
);

/** Negative keyword match types (Amazon enum values). */
export const NegativeMatchTypeSchema = z.enum(["NEGATIVE_PHRASE", "NEGATIVE_EXACT"]);
export type NegativeMatchType = z.infer<typeof NegativeMatchTypeSchema>;

export const OffAmazonSettingsSchema = z.object({
  expandedAudienceReach: z.boolean(),
});
export type OffAmazonSettings = z.infer<typeof OffAmazonSettingsSchema>;

function isCalendarDate(value: string): boolean {
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export const CalendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(isCalendarDate, {
    message: "Invalid calendar date",
  });

const CampaignCreationCampaignSchema = z
  .object({
    name: z.string().trim().min(1),
    portfolioId: z.string().nullable().optional(),
    startDate: CalendarDateSchema,
    endDate: CalendarDateSchema.nullable().optional(),
    dailyBudget: z.number().positive(),
    biddingStrategy: BiddingStrategySchema,
    /**
     * Amazon currently does not return this choice from Campaign list reads,
     * so it is an explicit write-only creation setting.
     */
    offAmazonSettings: OffAmazonSettingsSchema.optional(),
    placementAdjustments: z
      .array(
        z.object({
          placement: PlacementSchema,
          percentage: z.number().int().nonnegative(),
        }),
      )
      .default([]),
  })
  .superRefine((campaign, ctx) => {
    if (campaign.endDate && campaign.endDate < campaign.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "endDate must be on or after startDate",
        path: ["endDate"],
      });
    }
  });

/**
 * Validates and normalizes a payload already persisted for execution. It does
 * not reject a start date that became historical while the request was paused.
 */
export const CampaignCreationPersistedPayloadSchema = z.object({
  /**
   * Client-generated idempotency key. The same value must be reused across
   * double clicks, refreshes and network retries — it is what stops a second
   * Campaign from being created. Campaign name is NOT an idempotency signal.
   */
  requestId: z.string().uuid(),
  campaign: CampaignCreationCampaignSchema,
  adGroup: CampaignCreationAdGroupSchema,
  negatives: z
    .object({
      keywords: z
        .array(z.object({ text: z.string().min(1), matchType: NegativeMatchTypeSchema }))
        .default([]),
      productAsins: z.array(z.string().min(1)).default([]),
    })
    .default({ keywords: [], productAsins: [] }),
});

export const CampaignCreationSubmitSchema = CampaignCreationPersistedPayloadSchema.superRefine(
  (submission, ctx) => {
    const todayUtc = new Date().toISOString().slice(0, 10);
    if (submission.campaign.startDate < todayUtc) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "startDate cannot be in the past",
        path: ["campaign", "startDate"],
      });
    }
  },
);
export type CampaignCreationSubmit = z.infer<typeof CampaignCreationSubmitSchema>;

// ── Request status ──────────────────────────────────────────────

export const CampaignCreationStepNameSchema = z.enum([
  "activate_product_ads",
  "activate_targets",
  "activate_ad_groups",
  "activate_campaign",
  "activation_readback",
  "pause_campaign",
  "compensation_readback",
  "delivery_control",
  "delivery_readback",
  "campaign",
  "ad_group",
  "product_ads",
  "sb_ad",
  "target_bids",
  "creative",
  "negatives",
  "readback",
  "activation",
  "local_sync",
]);
export type CampaignCreationStepName = z.infer<typeof CampaignCreationStepNameSchema>;

/**
 * `partial` is a first-class outcome, not a flavour of failure: Amazon objects
 * already exist and the request can be resumed from the failed step.
 */
export const CampaignCreationStatusSchema = z.enum([
  "pending",
  "processing",
  "succeeded",
  "partial",
  "failed",
]);
export type CampaignCreationStatus = z.infer<typeof CampaignCreationStatusSchema>;

export const CampaignCreationRecoveryDispositionSchema = z.enum([
  "none",
  "resume",
  "edit",
  "manual_remediation",
  "reconcile",
]);
export type CampaignCreationRecoveryDisposition = z.infer<
  typeof CampaignCreationRecoveryDispositionSchema
>;

export const CampaignCreationStepRecoverySchema = z.enum([
  "none",
  "retryable",
  "requires_input",
  "reconcile",
]);
export type CampaignCreationStepRecovery = z.infer<typeof CampaignCreationStepRecoverySchema>;

export const CampaignCreationItemFailureSchema = z.object({
  payloadIndex: z.number().int().nonnegative(),
  errorCode: z.string(),
  reasonCode: z.string().nullable(),
  recovery: CampaignCreationStepRecoverySchema.exclude(["none"]),
});
export type CampaignCreationItemFailure = z.infer<typeof CampaignCreationItemFailureSchema>;

export const CampaignCreationFailureProductReferenceSchema = z.object({
  payloadIndex: z.number().int().nonnegative(),
  asin: z.string(),
  sku: z.string().nullable(),
});
export type CampaignCreationFailureProductReference = z.infer<
  typeof CampaignCreationFailureProductReferenceSchema
>;

export const CampaignCreationStepStatusSchema = z.object({
  step: CampaignCreationStepNameSchema,
  status: z.enum(["pending", "succeeded", "failed", "unknown"]),
  amazonObjectIds: z.array(z.string()),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  recovery: CampaignCreationStepRecoverySchema,
  itemFailures: z.array(CampaignCreationItemFailureSchema),
});
export type CampaignCreationStepStatus = z.infer<typeof CampaignCreationStepStatusSchema>;

export const CampaignCreationRequestStatusSchema = z
  .object({
    requestId: z.string(),
    adProduct: z.enum(["sp", "sb", "sd"]).optional(),
    status: CampaignCreationStatusSchema,
    amazonCampaignId: z.string().nullable(),
    localCampaignId: z.string().nullable(),
    steps: z.array(CampaignCreationStepStatusSchema),
    recoveryDisposition: CampaignCreationRecoveryDispositionSchema,
    failureProductReferences: z.array(CampaignCreationFailureProductReferenceSchema),
    /** Non-null only when persisted recoveryDisposition is `resume`. */
    resumableFrom: CampaignCreationStepNameSchema.nullable(),
  })
  .superRefine((status, ctx) => {
    if (status.recoveryDisposition === "resume" && status.resumableFrom == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "resume disposition requires resumableFrom",
        path: ["resumableFrom"],
      });
    }
    if (status.recoveryDisposition !== "resume" && status.resumableFrom != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "resumableFrom is only valid for resume disposition",
        path: ["resumableFrom"],
      });
    }
    if (status.status === "succeeded" && status.localCampaignId == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "succeeded requests require localCampaignId",
        path: ["localCampaignId"],
      });
    }
  });
export type CampaignCreationRequestStatus = z.infer<typeof CampaignCreationRequestStatusSchema>;
